import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../redux-store";
import type { SiteJobKind } from "./types";

const selectSiteJobEntities = (state: RootState) => state.data.siteJobs.entities;
const selectSiteJobIds = (state: RootState) => state.data.siteJobs.ids;

export const selectSiteJobs = createSelector(
  selectSiteJobEntities,
  selectSiteJobIds,
  (entities, ids) => ids.map((id) => entities[id]).filter((x) => !!x)
);

/** What the dispatch page shows on an order that is part of a site job. */
export type CargoSiteJobPosition = {
  siteJobId: string;
  kind: SiteJobKind;
  /** 1-based position among the orders of the group. */
  position: number;
  /** Number of orders in the group. */
  total: number;
  /** The cargos of the whole group, in order, so it can be handled as one. */
  cargoIds: string[];
};

/**
 * cargoId -> its position in its site job. Built once per site job load
 * instead of scanning the groups from every card.
 */
export const selectSiteJobPositionByCargoId = createSelector(
  selectSiteJobs,
  (siteJobs) => {
    const byCargoId: Record<string, CargoSiteJobPosition> = {};
    for (const siteJob of siteJobs) {
      const cargoIds = siteJob.orders.map((o) => o.cargoId);
      siteJob.orders.forEach((order, index) => {
        byCargoId[order.cargoId] = {
          siteJobId: siteJob.id,
          kind: siteJob.kind,
          position: index + 1,
          total: cargoIds.length,
          cargoIds,
        };
      });
    }
    return byCargoId;
  }
);

export const selectSiteJobPositionForCargo =
  (cargoId: string) =>
  (state: RootState): CargoSiteJobPosition | null =>
    selectSiteJobPositionByCargoId(state)[cargoId] ?? null;
